import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';

interface SignedAsset {
  url: string;
  expiresIn: number;
  bytes: number;
  mime: string;
}

/**
 * Resolves an asset id to a short-lived signed URL.
 *
 * The Storage bucket is private, so there is no stable URL to put in a `src` —
 * `GET /v1/media/:id/url` returns JSON carrying a signature the API mints after
 * checking `media@read`. A public bucket with unguessable paths would be
 * simpler and would mean a leaked URL is permanent access to a video a ministry
 * paid for.
 *
 * Cached for less than the signature's lifetime, so a learner scrolling a
 * lesson does not re-sign the same image on every render, and a link never goes
 * stale in place.
 */
export function useAssetUrl(assetId: string | undefined) {
  return useQuery({
    queryKey: ['asset-url', assetId],
    enabled: Boolean(assetId),
    staleTime: 50 * 60_000,
    gcTime: 55 * 60_000,
    retry: false,
    queryFn: () => apiClient.get<SignedAsset>(`/v1/media/${assetId}/url`),
  });
}
